import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { BaseViewComponent } from 'projects/shared/core/interfaces/baseViewComponent';
import { LookupService } from 'projects/shared/core/services/lookup.service';
import { ToastService } from 'projects/shared/core/services/toast.service';
import { UserModel } from 'projects/shared/core/app/api/models/user-model';
import { NdmsUsersService } from '../../../../shared/core-ndms/services/ndms-users.service';
import { ResponderService } from 'projects/rms/app/api/services/responder.service';
import { IdentityAccountModel } from '../../../../shared/core-ndms/models/identity-account.model';
import { NdmsRoleModel } from '../../../../shared/core-ndms/models/ndms-role.model';
import { RoleQueryResponseModel } from "projects/shared/core/app/api/models/role-query-response-model";
import { UserModelEditorViewModel } from "projects/shared/core/app/api/models/user-model-editor-view-model";
import { IdentityAccountsService } from '../../../../shared/core-ndms/services/identity-accounts.service';
import { confirmPasswordValidator } from '../../../../../../projects/shared/core/validators/confirm-password-validator.directive';

@Component({
  selector: 'user-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.scss']
})
export class UserEditComponent extends BaseViewComponent implements OnInit {
  @Output() complete = new EventEmitter<boolean>(false);

  private _identityAccount$: BehaviorSubject<IdentityAccountModel> = new BehaviorSubject<IdentityAccountModel>(undefined);
  get identityAccount$() {
    return this._identityAccount$.asObservable();
  }
  get identityAccount() {
    return this._identityAccount$.value;
  }

  public ndmsUser: UserModel = {
    key: '',
    userName: '',
    firstName: '',
    lastName: '',
    lastLoggedIn: ''
  };

  public get isNewAccount() {
    return !this.identityAccount && this.ndmsUser.key == '';
  }
  get identityAccountLockedOut() {
    return (this.identityAccount && this.identityAccount.lockedOut);
  }

  public form: FormGroup;
  public isEditing: boolean;
  public responseMessages: any[] = [];
  public passwordDoesNotMatch: boolean = false;
  public manualPasswordReset = false;
  public passwordResetComplete = false;
  public phoneEmpty: boolean;
  public applications: any = [];
  public isLoadingIdentityAccount: boolean = false;
  public isSendInvitationDisabled = false;
  public reset2FAComplete: boolean = false;
  public identityAdminRole: NdmsRoleModel = undefined;
  public userTeams: string = "None";
  public userOrganization: string = "None";
  public lastLoggedIn: string;

  constructor(
    private _formBuilder: FormBuilder,
    private _ndmsUsersService: NdmsUsersService,
    private _responderService: ResponderService,
    private _identityAccountsService: IdentityAccountsService,
    private _toastService: ToastService,
    lookupService: LookupService) {
    super(lookupService);
    this.applications = this.lookupService.get('applications');
    this.getUserIdentityAdminRole();
  }

  ngOnInit() {
    this.form = this._formBuilder.group({
      email: ['', Validators.required],
      firstName: [''],
      lastName: [''],
      phoneMobile: [''],
      organizations: [''],
      password: [''],
      passwordConfirm: [''],
      admin: false
    }, {
      validators: confirmPasswordValidator
    });
  }

  controlIsInvalid(control: string) {
    return (this.form.get(control).invalid && this.form.get(control).touched) || this.form.get(control).dirty;
  }

  controlHasError(control: string, condition: string) {
    return this.form.get(control).errors && this.form.get(control).errors[condition];
  }

  formatDate(loggedInDate: string): string {
    let dateString: string = "Not recorded";
    let date = new Date(loggedInDate);
    if (date.getFullYear() > 1) {
      dateString = date.toLocaleDateString() + " " + date.toLocaleTimeString();
    }
    return dateString;
  }

  getMask() {
    return { mask: ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/] };
  }

  passwordDoesNotConfirm() {
    return this.form.errors && this.form.errors.doesNotMatch;
  }

  // sendIdentityInvitation() {
  //   console.log(`send invitation to ${this.ndmsUser.userName}`)
  //   if (this.ndmsUser && this.ndmsUser.key && !this.isSendInvitationDisabled) {
  //     this._identityAccountsService.SendInvitation({
  //       email: this.ndmsUser.userName,
  //       firstName: this.ndmsUser.firstName,
  //       lastName: this.ndmsUser.lastName,
  //       phoneMobile: this.ndmsUser.phoneMobile
  //     }).subscribe(result => {
  //       this.isSendInvitationDisabled = result || false;
  //     });
  //   }
  // }

  onCompleteClick() {
    this.form.get('password').setErrors(null);
    this.form.get('passwordConfirm').setErrors(null);
    if (this.form.valid) {
      let ndmsUser: UserModel = {
        key: this.ndmsUser.key,
        userName: this.form.get('email').value,
        firstName: this.form.get('firstName').value,
        lastName: this.form.get('lastName').value,
        lastLoggedIn: this.lastLoggedIn
      };
      this._ndmsUsersService.saveUser(ndmsUser).then((data: UserModelEditorViewModel) => {
        if (data.responseMessages.length > 0) {
          this.responseMessages = data.responseMessages;
        }
        else {
          this.responseMessages = [];
          if (this.form.get('admin').value &&
              this.ndmsUser.userRoles.filter(role => role.roleName === "IdentityAdmin").length == 0) {
            this._ndmsUsersService.addRoleToUser(ndmsUser.key, this.identityAdminRole);
          }
          else if (!this.form.get('admin').value &&
                    this.ndmsUser.userRoles.filter(role => role.roleName === "IdentityAdmin").length > 0) {
            this._ndmsUsersService.removeRoleFromUser(ndmsUser.key, this.identityAdminRole);
          }
          if (this.identityAccount) {
            let identityAccount: IdentityAccountModel = Object.assign({}, this.identityAccount);
            identityAccount.email = this.form.get('email').value;
            identityAccount.firstName = this.form.get('firstName').value;
            identityAccount.lastName = this.form.get('lastName').value;
            identityAccount.password = this.form.get('password').value;
            identityAccount.confirmPassword = this.form.get('passwordConfirm').value;
            identityAccount.phoneMobile = this.form.get('phoneMobile').value;
            identityAccount.assignedApps = [];
            identityAccount.resetPasswordOnLogin = true;
            this.saveIdentityAccount(identityAccount)
              .then(() => {
                if (this.form.get('admin').value &&
                    identityAccount.roleNames.filter(role => role === "IdentityAdmin").length == 0) {
                  this.addIdentityRole(this.identityAccount);
                }
                else if (!this.form.get('admin').value &&
                          identityAccount.roleNames.filter(role => role === "IdentityAdmin").length > 0) {
                  this.removeIdentityRole(this.identityAccount);
                }
                if (this.responseMessages.length === 0) {
                  this.complete.emit(true);
                }
              })
          }
          else {
            this.complete.emit(true);
          }
        }
      });
    }
  }

  onCloseClick() {
    this.complete.emit(false);
  }

  onEmailChange() {
    if (!this.isNewAccount) {
      this.queryForIdentityAccount();
    }
  }

  onManualPasswordResetClick() {
    this.form.get('password').setValidators([Validators.required, Validators.pattern(/^((?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[^a-zA-Z0-9])).{14,}$/)]);
    this.form.get('passwordConfirm').setValidators([Validators.required]);

    this.manualPasswordReset = !this.manualPasswordReset;
  }

  onReset2FAClick() {
    if (confirm("Are you sure you want to reset Two-Factor Authentication?") && this.identityAccount) {
      this._identityAccountsService.Reset2FA(this.identityAccount.id).subscribe(result => {
        if (result) {
          this.reset2FAComplete = true;
        }
      });
    }
  }

  onResetPasswordClick() {
    if (confirm("Are you sure you want to reset the password?") && this.identityAccount) {
      this._identityAccountsService.ResetPassword(this.identityAccount.id).subscribe(result => {
        if (result) {
          this.passwordResetComplete = true;
        }
      });
    }
  }

  onUnlockClick() {
    if (this.identityAccount) {
      this._identityAccountsService.Unlock(this.identityAccount.id).subscribe(result => {
        if (result) {
          this.identityAccount.lockedOut = false;
        }
      });
    }
  }

  patchFormValues() {
    if (this.ndmsUser && Object.keys(this.ndmsUser).length > 0) {
      this.isEditing = false;
      if (this.ndmsUser.userName)
        this.isEditing = true;
      this.form.get('email').setValue(this.ndmsUser.userName);
      this.form.get('firstName').setValue(this.ndmsUser.firstName);
      this.form.get('lastName').setValue(this.ndmsUser.lastName);
      this.form.markAsTouched();
      if (!this.identityAccount && this.ndmsUser && this.ndmsUser.userName && !this.isLoadingIdentityAccount) {
        this.queryForIdentityAccount();
      }
      this.lastLoggedIn = this.ndmsUser.lastLoggedIn;
    }
  }

  private addIdentityRole(account: IdentityAccountModel) {
    let roleNames = ['IdentityAdmin'];
    return new Promise<void>((resolve, reject) => {
      this._identityAccountsService.AddUserToRoles(account.id, roleNames).subscribe(data => {
      resolve();
      }, error => {
        this._toastService.showError(error);
        reject(error);
      });
    });
  }

  private getResponderOrgTeams() {
    return new Promise<void>((resolve, reject) => {
      this.safeSubscribe(this._responderService.GetOrganizationTeams(this.ndmsUser.userName), data => {
        let userTeams = "";
        if (data) {
          this.userOrganization = data.organization.teamName;
          data.responderTeams.forEach(team => {
            userTeams += team.teamName + ", ";
          });
          if (userTeams) {
            this.userTeams = userTeams.substring(0, userTeams.length - 2);
          }
        }
        resolve();
      }, error => {
        this._toastService.showError(error);
        reject();
      });
    })
  }

  private getUserIdentityAdminRole() {
    this._ndmsUsersService.getRoles()
    .then((roles: RoleQueryResponseModel) => {
      let identityAdminRoles = roles.model.filter(role => role.roleName === "IdentityAdmin");
      if (identityAdminRoles && identityAdminRoles.length > 0) {
        let adminRole = identityAdminRoles[0];
        this.identityAdminRole = {
          roleKey: adminRole.roleKey,
          roleName: adminRole.roleName,
          displayName: adminRole.displayName
        }
      }
      else {
        this.identityAdminRole = undefined;
      }
    }).then(() => this.getResponderOrgTeams());
  }

  private queryForIdentityAccount() {
    if (!this.isLoadingIdentityAccount) {
      this.isLoadingIdentityAccount = true;
      let userName = this.form.get('email').value;
      this._identityAccountsService.Query({ email: userName }).subscribe(result => {
        if (result && result.model && result.model.length > 0) {
          this._identityAccount$.next(result.model[0]);
          this.form.get('phoneMobile').setValue(this.identityAccount.phoneMobile);
          this.form.get('admin').setValue(this.identityAccount.roleNames ?
            this.identityAccount.roleNames.filter(role => role === 'IdentityAdmin').length > 0 : false);
          this.phoneEmpty = false;
          if (!this.identityAccount.phoneMobile) this.phoneEmpty = true;
        }
        else {
          this._identityAccount$.next(undefined);
          this.form.get("admin").disable();
        }
        this.isLoadingIdentityAccount = false;
      });
    }
  }

  private removeIdentityRole(account: IdentityAccountModel) {
    let roleNames = ['IdentityAdmin'];
    return new Promise<void>((resolve, reject) => {
      if (account.roleNames.filter(role => role === "IdentityAdmin").length > 0) {
        this._identityAccountsService.RemoveUserFromRoles(account.id, roleNames).subscribe(data => {
          resolve();
        }, error => {
          this._toastService.showError(error);
          reject(error);
        });
      }
    });
  }

  private saveIdentityAccount(account: IdentityAccountModel) {
    return new Promise<IdentityAccountModel>((resolve, reject) => {
      this._identityAccountsService.Save(account, account.id).subscribe(data => {
        if (data.responseMessages.length > 0) {
          this.responseMessages = data.responseMessages;
        }
        else {
          this.responseMessages = [];
        }
        resolve(data);
      }, error => {
        this._toastService.showError(error);
        reject(error);
      });
    });
  }

}
